/**
 * TaglineRotator — rotating login taglines in a subtle quote card (BRAND-3c).
 *
 * A fixed-height translucent card (sized to the longest message so rotation never
 * reflows the layout) holds the current message in typographic quotes. Every 5s the
 * message SLIDES UP (outgoing translates up + fades, incoming enters from below,
 * ~400ms) and the pagination dots below advance. Pauses on hover/touch.
 * prefers-reduced-motion: no slide (instant swap) — dots still advance.
 * Messages are verbatim (no invented statistics).
 */
import { useState, useEffect, useRef, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';

// Fallback only (used if the namespace hasn't resolved). Live copy is common:login.taglines.
const MESSAGES = [
  "Schools that run on AI don't chase paper registers. The registers chase them.",
  'Homework excuses are getting smarter. Luckily, so is your school.',
  'Fee reminders, attendance, report cards — done before the morning bell.',
  "Your school's entire office, now lighter than a school bag.",
  'Teachers teach. Aisistoo handles the rest of the tab-switching.',
  "No more 'I forgot my Social Science notebook' surprises — everything lives in the app.",
];

const INTERVAL = 5000;
const SLIDE_MS = 400;

const SIZES = {
  mobile: { cardW: 300, cardH: 92, pad: '14px 22px', font: 12.5, radius: 14, dot: 6, dotGap: 7, wm: 72 },
  desktop: { cardW: 340, cardH: 118, pad: '18px 28px', font: 16, radius: 16, dot: 7, dotGap: 8, wm: 96 },
} as const;

interface TaglineRotatorProps {
  size?: 'mobile' | 'desktop';
  /** OLYMPIAD-P2D — override the rotating lines (e.g. olympiad facts on the signup page). */
  messages?: string[];
}

export function TaglineRotator({ size = 'desktop', messages }: TaglineRotatorProps) {
  const { t } = useTranslation();
  const cfg = SIZES[size];
  const fromI18n = t('common:login.taglines', { returnObjects: true });
  const defaultMsgs = Array.isArray(fromI18n) ? (fromI18n as string[]) : MESSAGES;
  const msgs = messages && messages.length > 0 ? messages : defaultMsgs;
  const [reduce, setReduce] = useState(false);
  const [idx, setIdx] = useState(0);
  const [prev, setPrev] = useState<number | null>(null);
  const [paused, setPaused] = useState(false);
  const prevIdxRef = useRef(0);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduce(mq.matches);
    const onChange = () => setReduce(mq.matches);
    mq.addEventListener?.('change', onChange);
    return () => mq.removeEventListener?.('change', onChange);
  }, []);

  // Advance the message every INTERVAL (unless paused). Dots follow `idx` always.
  useEffect(() => {
    if (paused) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % msgs.length), INTERVAL);
    return () => clearInterval(t);
  }, [paused]);

  // On message change, run the slide (skip when reduced-motion → instant swap).
  useEffect(() => {
    const from = prevIdxRef.current;
    prevIdxRef.current = idx;
    if (reduce || from === idx) return;
    setPrev(from);
    const t = setTimeout(() => setPrev(null), SLIDE_MS);
    return () => clearTimeout(t);
  }, [idx, reduce]);

  const msgStyle: CSSProperties = {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    textAlign: 'left',
    padding: cfg.pad,
    margin: 0,
    fontFamily: 'var(--font-heading)',
    fontSize: cfg.font + 1.5, // slightly larger (PTM-3 item 6)
    fontWeight: 600,
    fontStyle: 'italic',
    lineHeight: 1.45,
    letterSpacing: '-0.01em',
    color: 'rgba(255,255,255,0.95)',
    zIndex: 1,
  };

  return (
    <div
      className="login-tagline-rotator"
      style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={() => setPaused(true)}
      onTouchEnd={() => setPaused(false)}
    >
      <style>{`
        @keyframes trSlideIn  { from { transform: translateY(55%);  opacity: 0; } to { transform: translateY(0); opacity: 1; } }
        @keyframes trSlideOut { from { transform: translateY(0);    opacity: 1; } to { transform: translateY(-55%); opacity: 0; } }
      `}</style>

      <div
        style={{
          position: 'relative',
          width: cfg.cardW,
          maxWidth: '100%',
          height: cfg.cardH,
          overflow: 'hidden',
          borderRadius: cfg.radius,
          background: 'rgba(255,255,255,0.10)',
          border: '1px solid rgba(255,255,255,0.14)',
        }}
      >
        {/* PTM-3 item 6: clean pull-quote — a 3px left accent bar (white 40%), no
            overlapping glyphs. The message (italic, larger, left-aligned with
            generous padding) sits clear of the bar. */}
        <div aria-hidden style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 3, background: 'rgba(255,255,255,0.4)', zIndex: 0 }} />
        {!reduce && prev !== null ? (
          <>
            <p key={`out-${prev}`} aria-hidden style={{ ...msgStyle, animation: `trSlideOut ${SLIDE_MS}ms ease forwards` }}>
              {msgs[prev]}
            </p>
            <p key={`in-${idx}`} style={{ ...msgStyle, animation: `trSlideIn ${SLIDE_MS}ms ease forwards` }}>
              {msgs[idx]}
            </p>
          </>
        ) : (
          <p key={`static-${idx}`} style={msgStyle}>
            {msgs[idx]}
          </p>
        )}
      </div>

      <div style={{ display: 'flex', gap: cfg.dotGap }} aria-hidden>
        {msgs.map((_, i) => (
          <span
            key={i}
            style={{
              width: cfg.dot,
              height: cfg.dot,
              borderRadius: '50%',
              background: i === idx ? '#FFFFFF' : 'rgba(255,255,255,0.4)',
              transition: 'background 0.3s ease',
            }}
          />
        ))}
      </div>
    </div>
  );
}

export default TaglineRotator;
